import React from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Button from '../../components/ui/Button/Button';
import Pager from '../../components/ui/Pager/Pager';
import { SearchIcon, UsersIcon, FilePlusIcon } from '../../components/ui/Icons';
import { useLayout } from '../../layout/LayoutContext';
import { useAuth } from '../../../Components/AuthContext';
import hasPermission from '../../../Hooks/hasPermission';
import { useUsers } from './useUsers';
import UsersSkeleton from './UsersSkeleton';
import styles from './Users.module.css';

const CheckCircleIcon = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12 2.5 2.5 4.5-5" />
  </svg>
);
const BanCircleIcon = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="m5.6 5.6 12.8 12.8" />
  </svg>
);

const STAT_CARDS = [
  { key: 'all' as const, label: 'Total Users', color: 'var(--dylo-teal-700)', icon: <UsersIcon size={22} /> },
  { key: 'active' as const, label: 'Active', color: '#2aad27', icon: <CheckCircleIcon /> },
  { key: 'inactive' as const, label: 'Inactive', color: 'var(--dylo-danger)', icon: <BanCircleIcon /> },
];

const Users: React.FC = () => {
  const {
    users,
    loading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    counts,
    openDetail,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    filteredTotal,
  } = useUsers();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const canCreate = hasPermission(authUser?.permissionsActives, 'US_CREATE');

  const { setHideHeader } = useLayout();
  React.useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  // Skeleton solo en la carga inicial (sin datos aún); búsquedas usan el sweep
  if (loading && users.length === 0) {
    return <UsersSkeleton />;
  }

  return (
    <div className={`${styles.page} dylo-animate-in`}>
      <div className={styles.statGrid}>
        {STAT_CARDS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={[styles.statCard, statusFilter === c.key ? styles.statActive : ''].join(' ')}
            style={{ ['--stat-color' as any]: c.color }}
            onClick={() => setStatusFilter(c.key)}
          >
            <span className={styles.statIcon}>{c.icon}</span>
            <span className={styles.statText}>
              <span className={styles.statLabel}>{c.label}</span>
              <span className={styles.statValue}>
                {c.key === 'all' ? counts.total : c.key === 'active' ? counts.active : counts.inactive}
              </span>
            </span>
          </button>
        ))}
      </div>

      <Card className={loading ? 'dylo-loading-host' : undefined}>
        {loading && <span className="dylo-loading-sweep" aria-label="Loading" />}

        <div className={styles.header}>
          <h1 className={styles.title}>Users</h1>
          <div className={styles.headerActions}>
            <div className={styles.search}>
              <Input
                icon={<SearchIcon size={17} />}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canCreate && (
              <Button variant="primary" className={styles.newBtn} onClick={() => navigate('/users/new')}>
                <FilePlusIcon size={17} />
                New User
              </Button>
            )}
          </div>
        </div>

        <div className={styles.tableWrap}>
          <div className={styles.tableHead}>
            <span>Name</span>
            <span>Mail</span>
            <span className={styles.center}>Status</span>
            <span className={styles.center}>Loads</span>
            <span className={styles.center}>Actions</span>
          </div>

          <div className={styles.tableBody}>
            {users.length === 0 && !loading ? (
              <div className={styles.empty}>No users found.</div>
            ) : (
              users.map((u, i) => (
                <div
                  key={u.id}
                  className={styles.row}
                  style={{ animationDelay: `${Math.min(i, 12) * 0.03}s` }}
                >
                  <span className={styles.name}>{u.name}</span>
                  <span className={styles.mail}>{u.email}</span>
                  <span className={styles.center}>
                    <span
                      className={[styles.statusPill, u.status_id === 1 ? styles.active : styles.inactive].join(' ')}
                    >
                      {u.status_id === 1 ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </span>
                  <span className={[styles.center, styles.loads].join(' ')}>{u.loadCount ?? 0}</span>
                  <span className={styles.center}>
                    <Button variant="ghost" className={styles.detailBtn} onClick={() => openDetail(u.id)}>
                      Detail
                    </Button>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Sin resultados no se pinta: quedarían flechas inertes debajo de "No users found." */}
        {filteredTotal > 0 && (
          <Pager
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            total={filteredTotal}
            pageSize={pageSize}
            itemLabel="user"
            pageSizeOptions={[10, 20, 50]}
            onPageSizeChange={setPageSize}
          />
        )}
      </Card>
    </div>
  );
};

export default Users;
